import { log } from '@logtail/next';
import { AudioPreProcessingConfig } from 'app/components/PresetLab';
import _ from 'lodash';
import Meyda, { MeydaAudioFeature } from 'meyda';
import { adaptiveGate, cleanNumberArrayFrom } from './maths-utils';
import { addTimingInterval, newTimingRecord } from './timing';

function estimatePitchFromChroma(chroma: number[]): number {
    if (chroma.length !== 12) {
        throw new Error("Chroma array must contain exactly 12 values");
    }

    let weightedSum = 0;
    let totalWeight = 0;

    for (let i = 0; i < 12; i++) {
        weightedSum += i * chroma[i];
        totalWeight += chroma[i];
    }

    if (totalWeight === 0) {
        return 0;
    }

    let averagePitch = weightedSum / totalWeight;

    // Adjust for circular nature of pitch classes
    if (averagePitch > 11) {
        averagePitch -= 12;
    } else if (averagePitch < 0) {
        averagePitch += 12;
    }

    return averagePitch;
}


export function meyda_temporal(signal: Float32Array, sampleRate:number, bufferSize:number = 512) {

    // TODO take required params as an arg. We don't need to extract all of these since we'll typicallty only use 1 or 2.
    // We don't use/support the sprectrum ones yet : 'complexSpectrum', 'amplitudeSpectrum', 'powerSpectrum', 'mfcc', 'loudness',
    const features : MeydaAudioFeature[] = ['rms', 'energy', 'zcr', 'spectralCentroid', 'spectralFlatness', 'spectralRolloff', 'spectralSlope', 'spectralSpread', 'spectralSkewness', 'spectralKurtosis',  'perceptualSpread', 'perceptualSharpness',  'chroma'];

    function postProcessMeydaDatapoint(frame: Float32Array) {
        const meydaData = Meyda.extract(features, frame);
        let frameData;
        if (meydaData?.chroma) {
            const linearisedChroma = estimatePitchFromChroma(meydaData?.chroma);
            frameData = {
                ..._.omit(meydaData, 'chroma'),
                linearisedChroma
            };
        } else {
            frameData = meydaData;
        }
        // Remove any NaNs
        for (const key in frameData) {
            // @ts-expect-error - TODO being lazy
            if (_.isNaN(frameData[key])) {
                // @ts-expect-error - TODO being lazy
                frameData[key] = 0;
            }
        }

        return frameData;
    }

    Meyda.bufferSize = bufferSize;
    Meyda.sampleRate = sampleRate;

    const frames = Math.floor(signal.length / bufferSize);
    const remainder = signal.length % frames;
    const data = [];
    
    for (let i = 0; i < frames; i++) {
        const start = i * bufferSize;
        const end = start + bufferSize;
        const frame = signal.slice(start, end);
        data.push(postProcessMeydaDatapoint(frame));
    }

    // Can probably do a better job of analysing the remainder
    const paddedRemainder = new Float32Array(bufferSize).fill(0);
    for (let i = 0; i < remainder; i++) {
        paddedRemainder[i] = signal[frames * bufferSize + i];
    }
    data.push(postProcessMeydaDatapoint(paddedRemainder));

    return data;
}

const DEFAULT_ANALYSIS_CONFIGS : AudioPreProcessingConfig[] = [
    {
        name: 'amp',
        filter: {
            type: 'none',
            hz: 500,
            q: 1,
        },
        extractor: 'amplitude',
        gate: {
            enabled: false,
            hysteresisPc: 1,
            sustain: 2,
            targetEvents: 1,
            tolerance: 0.1,
            minGap: 2,
        },
        envelope: {
            enabled: true,
            attack: 0,
            decay: 4,
        },
        decimation: {
            strategy: 'peak',
        },
    },
    {
        name: 'pan',
        filter: {
            type: 'none',
            hz: 500,
            q: 1,
        },
        extractor: 'pan',
        gate: {
            enabled: false,
            hysteresisPc: 1,
            sustain: 2,
            targetEvents: 1,
            tolerance: 0.1,
            minGap: 2,
        },
        envelope: {
            enabled: true,
            attack: 0,
            decay: 4,
        },
        decimation: {
            strategy: 'window',
        },
    },
    // {
    //     name: 'pitch',
    //     filter: {
    //         type: 'none',
    //         hz: 500,
    //         q: 1,
    //     },
    //     extractor: 'pitch',
    //     gate: {
    //         enabled: false,
    //         hysteresisPc: 1,
    //         sustain: 2,
    //         targetEvents: 1,
    //         tolerance: 0.1,
    //     },
    //     envelope: {
    //         enabled: true,
    //         attack: 0,
    //         decay: 4,
    //     },
    //     decimation: {
    //         strategy: 'peak',
    //     },
    // },
];



// TODO: Caching.
export async function doAllAudioAnalysis(audioContext: AudioContext, originalBuffer: AudioBuffer, startTime: number, endTime: number, bpm: number, fps: number,  analysisConfigs: AudioPreProcessingConfig[], keepIntermediates = false) {
    
    const configs = analysisConfigs.concat(DEFAULT_ANALYSIS_CONFIGS);
    const truncatedDuration = endTime - startTime;
    const targetSamples = fps * truncatedDuration;
    
    // truncate and convert to mono and get panning data. The mono signal is re-used across all extractions which is why we do it at the top level.
    const {monoBuffer, balanceArray} = truncateAndConvertToMonoWithBalance(audioContext, originalBuffer, startTime, endTime);

    const result : Record<string, number[]> = {};
    const errors : any[] = [];
    const allIntermediates : Record<string, Float32Array[]> = {};
    await Promise.all(configs.map(async (config) => {
        const {final, intermediates, error } = await doSingleAudioAnalysis(config, monoBuffer, balanceArray, targetSamples, bpm);
        if (error) {
            errors.push(error);
        }
        result[config.name] = final;
        if (keepIntermediates) {
            allIntermediates[config.name] = intermediates;
        }
    }));

    return {result, errors, intermediates: allIntermediates};
}


export async function doSingleAudioAnalysis(config: AudioPreProcessingConfig, monoBuffer: AudioBuffer, balanceArray: Float32Array, targetSamples: number, bpm: number) {
    const timing = newTimingRecord(`audioProcessing-` + config.name, {durationS: monoBuffer.duration});
    try {
        // Filter
        const filteredAudioBuffer = (config.filter.type !== 'none')
            ? await filterAudio(monoBuffer, config.filter.type, config.filter.hz, config.filter.q)
            : monoBuffer;
        addTimingInterval(timing, 'filter');

        // Signal extraction
        let extractedSignal: Float32Array;
        switch (config.extractor) {
            case 'amplitude':
                extractedSignal = filteredAudioBuffer.getChannelData(0);
                break;
            case 'pan':
                extractedSignal = balanceArray;
                break;
            default:
                // TODO - we extract many signals here and use at most one
                const meydaData = meyda_temporal(filteredAudioBuffer.getChannelData(0), filteredAudioBuffer.sampleRate, 512);
                //@ts-expect-error - TODO - being lazy
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                const meydaSignal = meydaData.map((d) => d[config.extractor]) as number[];
                extractedSignal = Float32Array.of(...meydaSignal);
        }
        addTimingInterval(timing, 'extract');

        // Decimation 
        const decimationStrategy = decimationStrategyFromName(config.decimation.strategy);
        const decimatedSignal = decimationStrategy.decimate(extractedSignal, targetSamples);
        addTimingInterval(timing, 'decimation');

        // Gate
        const gateConfig = _.cloneDeep(config.gate);
        gateConfig.targetEvents = Math.max(1, Math.round(gateConfig.targetEvents * bpm / 60)); // convert events-per-beat to total events
        const [gatedSignal, optimalThreshold, actualOpenings, attempts, gateEvents] = config.gate.enabled ? adaptiveGate(decimatedSignal, gateConfig) : [decimatedSignal, 0, 0, 0, []];
        config.gate.enabled && log.debug("Gate results: ", { targetOpenings: gateConfig.targetEvents, actualOpenings, optimalThreshold, attempts });
        addTimingInterval(timing, 'gate');

        // Apply envelope
        const smoothedSignal = config.envelope.enabled ? envelope(gatedSignal, config.envelope.attack, config.envelope.decay) : gatedSignal;
        addTimingInterval(timing, 'envelope');

        // Normalise
        const normalisedSignal = normalise(smoothedSignal);
        addTimingInterval(timing, 'normalisation');

        return {
            final: cleanNumberArrayFrom(normalisedSignal),
            intermediates : [filteredAudioBuffer.getChannelData(0), extractedSignal],
            gateOpenings: actualOpenings,
            gateEvents,          
            error: null,
        };

    } catch (e) {
        log.error(`Error processing audio for ${config.name}:`, { error: e });
        const zeros = new Float32Array(targetSamples).fill(0);
        return {
            final: Array.from(new Float32Array(balanceArray.length).fill(0)),
            intermediates: [zeros, zeros],
            error: e
        };
        
    } finally {
        log.info(`Audio processing timing for ${config.name}: [${(timing.last - timing.start).toFixed(2)}]ms. `, { timing });
    }
}


export async function detectEvents(monoBuffer: AudioBuffer, targetSamples: number, bpm:number, targetEventsPerBeat: number, method: 'energy'|'amplitude') {

    const targetEvents = targetEventsPerBeat * (monoBuffer.duration/60) * bpm;

    const eventDetectionConfig = {
        name: 'events',
        filter: {
            type: 'none' as const,
            hz: 500,
            q: 1,
        },
        extractor: method,
        decimation: {
            strategy: 'peak' as const,
        },        
        gate: {
            enabled: true,
            targetEvents: targetEvents,
            tolerance: 0.1,
            hysteresisPc: 0,
            sustain: 0,
            minGap: 2,
        },
        envelope: {
            enabled: false,
            attack: 0,
            decay: 0,
        },

    }


    const {gateOpenings, gateEvents} = await doSingleAudioAnalysis(eventDetectionConfig, monoBuffer, new Float32Array(monoBuffer.length).fill(0), targetSamples, bpm);

    if (!gateOpenings || gateOpenings <= 1) {
        return {events: [], error: "No events detected"};
    }

    const events = gateEvents.map((e) => ({
        t: e.peakPosition / targetSamples * monoBuffer.duration,
        strength: e.peakValue,
    }));
    return {events, error:null};


}


export function truncateAndConvertToMonoWithBalance(
    audioContext: AudioContext,
    originalBuffer: AudioBuffer,
    startTime: number,
    endTime: number
): { monoBuffer: AudioBuffer; balanceArray: Float32Array } {
    const timing = newTimingRecord(`audioProcessing-common`, {originalDurationS: originalBuffer.length, durationS: endTime-startTime});    


    const { sampleRate, duration } = originalBuffer;

    const startSample = Math.floor(Math.max(0, startTime) * sampleRate);
    const endSample = Math.floor(Math.min(duration, endTime) * sampleRate);

    const frameCount = endSample - startSample;
    const monoBuffer = audioContext.createBuffer(1, frameCount, sampleRate);
    const balanceArray = new Float32Array(frameCount);

    // Create a typed array to hold the mixed data
    const monoData = monoBuffer.getChannelData(0);

    let left, right: Float32Array;
    switch (originalBuffer.numberOfChannels) {
        case 1:
            left = originalBuffer.getChannelData(0).subarray(startSample, endSample);
            right = left;
            break;
        case 2:
            left = originalBuffer.getChannelData(0).subarray(startSample, endSample);
            right = originalBuffer.getChannelData(1).subarray(startSample, endSample);
            break;
        case 6:
            // Use front-left and front-right for 5.1 audio
            left = originalBuffer.getChannelData(0).subarray(startSample, endSample);
            right = originalBuffer.getChannelData(1).subarray(startSample, endSample);
            break;
        default:
            // Assume left/right in first positions for other cases
            left = originalBuffer.getChannelData(0).subarray(startSample, endSample);
            right = originalBuffer.getChannelData(1)?.subarray(startSample, endSample) || left;
    }

    for (let i = 0; i < frameCount; i++) {
        // Mid + side processing to avoid phase cancellation when merging stereo channels
        const mid = (left[i] + right[i]) / 2;
        const side = (left[i] - right[i]) / 2;
        let mono = mid + side;
        
        // Dodge corrupt audio samples
        if (isNaN(mono)) {
            mono = (i > 0) ? monoData[i - 1] : 0;
        }   
        monoData[i] = mono;

        // Calculate balance
        const leftAbs = Math.abs(left[i]);
        const rightAbs = Math.abs(right[i]);
        const total = leftAbs + rightAbs;
        
        if (total > 0) {
            // Map balance to range -1 (full left) to 1 (full right)
            balanceArray[i] = (rightAbs - leftAbs) / total;
        } else {
            balanceArray[i] = 0; // Center if both channels are silent
        }
    }

    addTimingInterval(timing, 'truncateAndMono');
    log.info(`Audio processing timing for mono conversion [${(timing.last - timing.start).toFixed(2)}]ms.`, {timing});

    return { monoBuffer, balanceArray };
}


// Abstract base class for decimation algorithms
abstract class DecimationStrategy {
    abstract decimate(signal: Float32Array, targetSamples: number): Float32Array;
}

class LinearInterpolationStrategy extends DecimationStrategy {
    decimate(signal: Float32Array, targetSamples: number): Float32Array {
        if (targetSamples <= 0) {
            throw new Error("Target samples must be greater than 0");
        }
        const output = new Float32Array(Math.ceil(targetSamples));
        const step = (signal.length - 1) / (targetSamples - 1);
        
        for (let i = 0; i < targetSamples; i++) {
            const exactIndex = i * step;
            const lowIndex = Math.floor(exactIndex);
            const highIndex = Math.ceil(exactIndex);
            
            if (lowIndex === highIndex) {
                output[i] = signal[lowIndex];
            } else {
                const fraction = exactIndex - lowIndex;
                output[i] = (1 - fraction) * signal[lowIndex] + fraction * signal[highIndex];
            }
        }
        
        return output;
    }
}

class WindowAveragingStrategy extends DecimationStrategy {
    decimate(signal: Float32Array, targetSamples: number): Float32Array {
        if (targetSamples <= 0) {
            throw new Error("Target samples must be greater than 0");
        }
        const output = new Float32Array(Math.ceil(targetSamples));
        const step = signal.length / targetSamples;
        
        for (let i = 0; i < targetSamples; i++) {
            let sum = 0;
            const start = Math.floor(i * step);
            const end = Math.floor((i + 1) * step);
            for (let j = start; j < end; j++) {
                sum += signal[j];
            }
            output[i] = sum / (end - start);
        }
        
        return output;
    }
}

class MedianFilterStrategy extends DecimationStrategy {
    decimate(signal: Float32Array, targetSamples: number): Float32Array {
        if (targetSamples <= 0) {
            throw new Error("Target samples must be greater than 0");
        }
        const output = new Float32Array(Math.ceil(targetSamples));
        const step = signal.length / targetSamples;
    
        for (let i = 0; i < targetSamples; i++) {
            const start = Math.floor(i * step);
            const end = Math.floor((i + 1) * step);
            const window = signal.slice(start, end);
            window.sort((a, b) => a - b);
            output[i] = window[Math.floor(window.length / 2)];
        }
        
        return output;
    }
}

class RMSStrategy extends DecimationStrategy {
    decimate(signal: Float32Array, targetSamples: number): Float32Array {
        if (targetSamples <= 0) {
            throw new Error("Target samples must be greater than 0");
        }
        const output = new Float32Array(Math.ceil(targetSamples));
        const step = signal.length / targetSamples;
        
        for (let i = 0; i < targetSamples; i++) {
            let sumSquares = 0;
            const start = Math.floor(i * step);
            const end = Math.floor((i + 1) * step);
            for (let j = start; j < end; j++) {
                sumSquares += signal[j] * signal[j];
            }
            output[i] = Math.sqrt(sumSquares / (end - start));
        }
        
        return output;
    }
}

class ZeroCrossingRateStrategy extends DecimationStrategy {
    decimate(signal: Float32Array, targetSamples: number): Float32Array {
        if (targetSamples <= 0) {
            throw new Error("Target samples must be greater than 0");
        }
        const output = new Float32Array(Math.ceil(targetSamples));
        const step = signal.length / targetSamples;
        
        for (let i = 0; i < targetSamples; i++) {
            let crossings = 0;
            const start = Math.floor(i * step);
            const end = Math.floor((i + 1) * step);
            for (let j = start + 1; j < end; j++) {
                if ((signal[j] >= 0 && signal[j - 1] < 0) || 
                    (signal[j] < 0 && signal[j - 1] >= 0)) {
                    crossings++;
                }
            }
            output[i] = crossings / (end - start);
        }
        
        return output;
    }
}

class PeakPreservingStrategy extends DecimationStrategy {
    decimate(signal: Float32Array, targetSamples: number): Float32Array {
        if (targetSamples <= 0) {
            throw new Error("Target samples must be greater than 0");
        }
        const output = new Float32Array(Math.ceil(targetSamples));
        const step = signal.length / targetSamples;
        
        for (let i = 0; i <targetSamples; i++) {
            let max = -Infinity;
            const start = Math.floor(i * step);
            const end = Math.floor((i + 1) * step);
            for (let j = start; j < end; j++) {
                max = Math.max(max, Math.abs(signal[j]));
            }           
            output[i] = max;
        }
        
        return output;
    }
}


export const linearDecimationStrategy = new LinearInterpolationStrategy();
export const windowDecimationStrategy = new WindowAveragingStrategy();
export const peakPreservingStrategy = new PeakPreservingStrategy();
export const medianStrategy = new MedianFilterStrategy();
export const rmsStrategy = new RMSStrategy();
export const zeroCrossingStrategy = new ZeroCrossingRateStrategy();

export type decimationStrategyName = 'linear' | 'window' | 'peak' | 'median' | 'rms' | 'zcr';

export const decimationStrategyFromName = (name: decimationStrategyName) => {
    switch (name) {
        case 'linear':
            return linearDecimationStrategy;
        case 'window':
            return windowDecimationStrategy;
        case 'peak':
            return peakPreservingStrategy;
        case 'median':
            return medianStrategy;
        case 'rms':
            return rmsStrategy;
        case 'zcr':
            return zeroCrossingStrategy;
    }
}

export async function filterAudio(audioBuffer: AudioBuffer, filterType : BiquadFilterType, hz: number, q:number): Promise<AudioBuffer> {

    const channelCount = audioBuffer.numberOfChannels;
    const originalSampleRate = audioBuffer.sampleRate;

    // Set up source for offline lowpass filtering (to avoid aliasing on decimation)
    const offlineContext = new OfflineAudioContext(channelCount, audioBuffer.length, originalSampleRate);
    const offlineSource = offlineContext.createBufferSource();
    offlineSource.buffer = audioBuffer;
    
    // Set up and apply offline filter
    const filter = offlineContext.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = hz;
    filter.Q.value = q;
    offlineSource.connect(filter);
    filter.connect(offlineContext.destination);
    offlineSource.start();
    const filteredBuffer = await offlineContext.startRendering();

    return filteredBuffer;
}


export function audioDownsample(signalAudioBuffer: AudioBuffer, targetSamples: number, decimationStrategy: DecimationStrategy = new WindowAveragingStrategy()): Float32Array[] {
    const decimatedSignals = [];
    for (let channel = 0; channel < signalAudioBuffer.numberOfChannels; channel++) {
        const filteredSignal = signalAudioBuffer.getChannelData(channel);
        const decimatedSignal = decimationStrategy.decimate(filteredSignal, targetSamples);
        decimatedSignals.push(decimatedSignal);
    }
    return decimatedSignals;
}

export function envelope(signal: Float32Array, attackTime: number = 0.001, releaseTime: number = 0.1) {
    const envelope = new Float32Array(signal.length);
    const attackCoeff = Math.exp(-1 / attackTime);
    const releaseCoeff = Math.exp(-1 / releaseTime);

    let envelopeValue = 0;
    for (let i = 0; i < envelope.length; i++) {
        if (signal[i] > envelopeValue) {
            envelopeValue = attackCoeff * envelopeValue + (1 - attackCoeff) * signal[i];
        } else {
            envelopeValue = releaseCoeff * envelopeValue + (1 - releaseCoeff) * signal[i];
        }
        envelope[i] = envelopeValue;
    }

    return envelope;
}

export function normalise(array: Float32Array) {
    if (array.length === 0) {
        return new Float32Array();
    }

    let min = array[0];
    let max = array[0];

    // Find min and max values
    for (let i = 1; i < array.length; i++) {
        if (array[i] < min) min = array[i];
        if (array[i] > max) max = array[i];
    }

    // If all values are the same, return an array of 1s
    if (min === max) {
        return new Float32Array(array.length).fill(1);
    }

    // Normalize the array
    const range = max - min;
    return array.map(value => (value - min) / range);
}

export function monoToAudioBuffer(audioContext: AudioContext, monoSignal: Float32Array, durationInSeconds: number): AudioBuffer {
    // Validate input
    if (!(audioContext instanceof AudioContext)) {
        throw new Error('Invalid AudioContext provided');
    }

    if (!(monoSignal instanceof Float32Array)) {
        throw new Error('Mono signal must be a Float32Array');
    }

    if (typeof durationInSeconds !== 'number' || durationInSeconds <= 0) {
        throw new Error('Invalid duration');
    }

    const MIN_SAMPLE_RATE = 3000;
    const MAX_SAMPLE_RATE = 192000;

    // Calculate the initial sample rate
    let sampleRate = Math.round(monoSignal.length / durationInSeconds);

    // Check if upsampling is needed
    if (sampleRate < MIN_SAMPLE_RATE) {
        log.warn(`Original sample rate (${sampleRate} Hz) is too low. Upsampling to ${MIN_SAMPLE_RATE} Hz.`);
        sampleRate = MIN_SAMPLE_RATE;
        
        // Calculate the number of samples needed for the new sample rate
        const targetLength = Math.ceil(MIN_SAMPLE_RATE * durationInSeconds);
        
        // Upsample the signal using last-seen value algorithm
        const upsampledSignal = new Float32Array(targetLength);
        for (let i = 0; i < targetLength; i++) {
            const sourceIndex = Math.floor(i * monoSignal.length / targetLength);
            upsampledSignal[i] = monoSignal[sourceIndex];
        }
        
        monoSignal = upsampledSignal;
    } else if (sampleRate > MAX_SAMPLE_RATE) {
        throw new Error(`Calculated sample rate (${sampleRate} Hz) exceeds maximum allowed rate`);
    }

    // Create an AudioBuffer
    let audioBuffer: AudioBuffer;
    try {
        audioBuffer = audioContext.createBuffer(1, monoSignal.length, sampleRate);
    } catch (e) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        throw new Error(`Failed to create AudioBuffer: ${e}`);
    }

    // Copy the mono signal to the AudioBuffer
    const channelData = audioBuffer.getChannelData(0);
    channelData.set(monoSignal);

    // Log the actual duration
    const actualDuration = audioBuffer.duration;
    log.debug(`Requested duration: ${durationInSeconds}s, Actual duration: ${actualDuration}s`);
    log.debug(`Final sample rate: ${sampleRate} Hz`);

    return audioBuffer;
}


export function audioBufferToDataUrl(audioBuffer: AudioBuffer): Promise<string> {
    const wavData = audioBufferToWav(audioBuffer);
    const blob = new Blob([wavData], { type: 'audio/wav' });
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

function audioBufferToWav(audioBuffer: AudioBuffer): ArrayBuffer {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;

    const bytesPerSample = bitDepth / 8;
    const blockAlign = numberOfChannels * bytesPerSample;

    const bufferLength = audioBuffer.length;
    const wavDataBytes = bufferLength * blockAlign;
    const wavHeaderBytes = 44;
    const totalBytes = wavHeaderBytes + wavDataBytes;

    const buffer = new ArrayBuffer(totalBytes);
    const view = new DataView(buffer);

    // WAV header
    writeString(view, 0, 'RIFF');
    view.setUint32(4, totalBytes - 8, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, wavDataBytes, true);

    // WAV data
    const channels = [];
    for (let i = 0; i < numberOfChannels; i++) {
        channels.push(audioBuffer.getChannelData(i));
    }

    let offset = 44;
    for (let i = 0; i < bufferLength; i++) {
        for (let channel = 0; channel < numberOfChannels; channel++) {
            const sample = Math.max(-1, Math.min(1, channels[channel][i]));
            view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
            offset += 2;
        }
    }

    return buffer;
}

function writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
    }
}
